import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
/**
 * The landing hero used at the top of a section — a framed icon tile, a small
 * pill above the title, a large title and a blurb, inside a panel with a faint
 * accent wash.
 *
 * The wash is two radial bleeds at 4–5% alpha over a vertical surface ramp. It
 * has to stay that subtle: anything stronger and the panel starts competing with
 * the numbers on the page, which are the reason anyone opened it.
 */
export function PageHero({
  icon: Icon, chip = 'Workspace', title, blurb, actions, compact = false, backTo, backLabel = 'Back',
}) {
  return (
    <section
      /*
       * The hero owns the gap beneath it. PageHeader used to carry this margin,
       * and losing it on the swap left every page's hero jammed against whatever
       * followed.
       */
      className={`panel-gradient relative overflow-hidden rounded-2xl ${
        compact ? 'mb-5 p-4 sm:p-5' : 'mb-6 p-6 sm:p-8'
      }`}
    >
      {backTo && (
        <Link
          to={backTo}
          className="relative mb-3 inline-flex items-center gap-1 text-xs font-medium text-muted transition-colors hover:text-ink"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
          {backLabel}
        </Link>
      )}

      <div className="relative flex flex-wrap items-center gap-4">
        {Icon && (
          <div
            className={`${compact ? 'h-11 w-11' : 'h-14 w-14'} grid shrink-0 place-items-center rounded-xl`}
            style={{
              background: 'rgb(var(--surface))',
              color: 'rgb(var(--ink))',
              border: '1px solid rgb(var(--line))',
              boxShadow: 'var(--shadow-xs)',
            }}
            aria-hidden="true"
          >
            <Icon className={compact ? 'h-5 w-5' : 'h-6 w-6'} />
          </div>
        )}

        <div className="min-w-0 flex-1">
          {chip && (
            <div
              className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-accent-200 px-2.5 py-0.5 text-[11px] font-medium text-accent-ink shadow-sm"
              style={{ background: 'rgb(var(--surface))' }}
            >
              {chip}
            </div>
          )}
          <h1
            className={`${compact ? 'text-xl' : 'text-2xl lg:text-3xl'} font-semibold tracking-tight text-ink`}
          >
            {title}
          </h1>
          {blurb && <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-ink-soft">{blurb}</p>}
        </div>

        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </section>
  );
}

export default PageHero;
